import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { EntityKind, RiskRow, RiskSignal, RiskSource } from '@regigov/contracts';
import { riskApi } from '../api/compliance';
import { ProblemAlert, formatDateTime } from './ui';

export const SOURCE_LABEL: Record<RiskSource, string> = {
  monitoring_hit: 'Monitoring hit',
  incident: 'Open incident',
  recall_unit: 'Recall unit',
  reconciliation: 'Reconciliation',
  access_denied: 'Access denied',
};
const SOURCES = Object.keys(SOURCE_LABEL) as RiskSource[];
export const REGISTER_PATH: Record<EntityKind, string> = { model: 'models', unit: 'units', installation: 'installations' };
export const SOURCE_PATH: Record<RiskSignal['sourceRecord']['kind'], ((id: string) => string) | null> = {
  monitoring_run: (id) => `/cer/compliance/monitoring/runs/${id}`,
  incident: (id) => `/cer/incidents/${id}`,
  recall: (id) => `/cer/recalls/${id}`,
  reconciliation_run: (id) => `/cer/exchange/reconciliation/${id}`,
  audit_event: null,
};

function SourceLink({ record }: { record: RiskSignal['sourceRecord'] }) {
  const path = SOURCE_PATH[record.kind]?.(record.id);
  return path ? <Link to={path}>{record.label}</Link> : <code>{record.label}</code>;
}

/** CER risk view (compliance demo C2): live signals weighted per source and grouped by participant. Advisory only. */
export function RiskPage() {
  const risk = useQuery({ queryKey: ['compliance', 'risk'], queryFn: riskApi.view, staleTime: 0, gcTime: 0 });
  const v = risk.data;
  return (
    <>
      <div className="row">
        <h1>Risk view</h1>
        <Link to="/cer/compliance/monitoring">← Monitoring rules &amp; runs</Link>
        <Link to="/cer/compliance/cases">Cases</Link>
      </div>
      <p className="notice">
        Advisory only: nothing here updates the register. Each participant's score is the sum of the weights of its live signals, computed on request from the latest run of each
        monitoring rule, unresolved incidents, recall units not yet verified or closed, unreviewed or accepted differences in the latest reconciliation of each partner interface,
        and denied access attempts since {v ? formatDateTime(v.accessDeniedSince) : '…'}. Every signal links to its source record.
      </p>
      <ProblemAlert error={risk.error} />
      {v && (
        <>
          <p className="muted">
            Weights: {SOURCES.map((s) => `${SOURCE_LABEL[s]} ${v.weights[s]}`).join(' · ')}. Generated {formatDateTime(v.generatedAt)}.
          </p>
          {v.rows.length === 0 ? (
            <p>No live signals.</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Participant</th>
                  <th>Score</th>
                  {SOURCES.map((s) => (
                    <th key={s}>{SOURCE_LABEL[s]}</th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {v.rows.map((r) => (
                  <RiskRowView key={r.participantId ?? 'unattributed'} row={r} />
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </>
  );
}

function RiskRowView({ row }: { row: RiskRow }) {
  return (
    <>
      <tr>
        <td>{row.participantId ? <Link to={`/cer/participants/${row.participantId}`}>{row.participantName ?? row.participantId}</Link> : <em>Unattributed</em>}</td>
        <td>
          <strong>{row.score}</strong>
        </td>
        {SOURCES.map((s) => (
          <td key={s}>{row.counts[s] || <span className="muted">0</span>}</td>
        ))}
        <td>{row.participantId && <Link to={`/cer/compliance/cases/new?participant=${encodeURIComponent(row.participantId)}`}>Open case</Link>}</td>
      </tr>
      <tr>
        <td colSpan={3 + SOURCES.length}>
          <details>
            <summary>
              {row.signalCount > row.signals.length
                ? `Showing the ${row.signals.length} heaviest and newest of ${row.signalCount} signals`
                : `${row.signalCount} signal${row.signalCount === 1 ? '' : 's'}`}
            </summary>
            <table className="table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Weight</th>
                  <th>Signal</th>
                  <th>Observed</th>
                  <th>Source record</th>
                  <th>Register record</th>
                </tr>
              </thead>
              <tbody>
                {row.signals.map((s) => (
                  <tr key={`${s.source}:${s.sourceId}`}>
                    <td>{SOURCE_LABEL[s.source]}</td>
                    <td>{s.weight}</td>
                    <td>
                      {s.title}
                      <div className="muted">{s.detail}</div>
                    </td>
                    <td>{formatDateTime(s.observedAt)}</td>
                    <td>
                      <SourceLink record={s.sourceRecord} />
                    </td>
                    <td>{s.registerRecord ? <Link to={`/cer/register/${REGISTER_PATH[s.registerRecord.kind]}/${s.registerRecord.id}`}>{s.registerRecord.label}</Link> : <span className="muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </td>
      </tr>
    </>
  );
}
